import { logError } from "@/lib/log";
import { fmtMoney } from "@/lib/money";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { UtensilsCrossed, FolderTree, QrCode, ArrowRight, ExternalLink, Sparkles, TrendingUp, CreditCard, ShoppingBasket, Sun, Truck, ClipboardList, Armchair, CalendarDays, Clock, Printer, Settings as SettingsIcon } from "lucide-react";
import { OptionBanners } from "@/components/OptionBanners";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { FoodTruckDialog } from "@/components/FoodTruckDialog";
import { ExportCard } from "@/components/ExportCard";
import { InstallAppCard } from "@/components/InstallAppButton";
import { PushNotificationsCard } from "@/components/PushNotificationsCard";
import { menuLink } from "@/lib/publicUrl";

export default function Overview() {
  const { user } = useAuth();
  const [restaurant, setRestaurant] = useState(null);
  const [stats, setStats] = useState({ categories: 0, dishes: 0 });
  const [orderStats, setOrderStats] = useState(null);
  const [connect, setConnect] = useState(null);
  const [bills, setBills] = useState(null);
  const feats = user?.subscription?.features || {};
  const canPay = !!feats.pay_bill;
  const canOrder = !!user?.subscription?.features?.ordering;

  useEffect(() => {
    (async () => {
      try {
        const [r, cats, dishes] = await Promise.all([
          api.get("/restaurant"), api.get("/categories"), api.get("/dishes"),
        ]);
        setRestaurant(r.data);
        setStats({ categories: cats.data.length, dishes: dishes.data.length });
      } catch (e) {
        logError("Caricamento panoramica fallito:", e);
        toast.error("Impossibile caricare la panoramica. Riprova.");
      }
      if (canOrder) {
        try { setOrderStats((await api.get("/stats/orders")).data); }
        catch (e) { logError("Statistiche ordini non disponibili:", e); }
      }
      if (canPay) {
        try { setConnect((await api.get("/connect/status")).data); }
        catch (e) { logError("Stato Stripe Connect non disponibile:", e); }
        try { setBills((await api.get("/payments/bills")).data); }
        catch (e) { logError("Incassi al tavolo non disponibili:", e); }
      }
    })();
  }, [canPay, canOrder]);

  const menuUrl = restaurant ? menuLink(restaurant.slug) : "";

  return (
    <div className="space-y-8" data-testid="overview-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-display font-bold text-4xl">Ciao, {user?.name?.split(" ")[0]} 👋</h1>
          <p className="text-gray-500 mt-1">Ecco una panoramica del tuo menù digitale.</p>
        </div>
        {restaurant && user?.is_paid && <FoodTruckDialog restaurant={restaurant} onSaved={setRestaurant} />}
      </div>
      <OptionBanners testid="overview-banners" items={[
        { label: "Costruttore menù", hint: "Piatti e categorie", icon: UtensilsCrossed, to: "/dashboard/menu" },
        !!feats.daily_menu && { label: "Menù del giorno", hint: "Il piatto di oggi", icon: Sun, to: "/dashboard/daily" },
        canOrder && { label: "Ordini", hint: "Tavolo e asporto", icon: ShoppingBasket, to: "/dashboard/orders" },
        !!feats.tables && { label: "Comande", hint: "Prendi la comanda", icon: ClipboardList, to: "/dashboard/comande" },
        !!feats.tables && { label: "Tavoli", hint: "Libero / occupato", icon: Armchair, to: "/dashboard/tables" },
        !!feats.tables && { label: "Calendario", hint: "Prenotazioni", icon: CalendarDays, to: "/dashboard/calendario" },
        !!feats.waitlist && { label: "Lista d'attesa", hint: "Clienti in coda", icon: Clock, to: "/dashboard/waitlist" },
        !!feats.foodtruck && { label: "FoodTruck", hint: "Tappe e orari", icon: Truck, to: "/dashboard/foodtruck" },
        { label: "QR Code", hint: "Stampa e link", icon: QrCode, to: "/dashboard/qr" },
        !!feats.tables && { label: "Stampanti", hint: "Cucina e bar", icon: Printer, to: "/dashboard/settings#sez-stampanti" },
        { label: "Impostazioni", hint: "Locale e servizi", icon: SettingsIcon, to: "/dashboard/settings" },
        { label: "Abbonamento", hint: "Piano e fatture", icon: CreditCard, to: "/dashboard/billing" },
      ]} />

      {restaurant?.suspended && (
        <div className="rounded-2xl border border-red-300 bg-red-50 p-5" data-testid="suspended-banner">
          <p className="font-semibold text-red-800">Account sospeso</p>
          <p className="text-red-700 text-sm">Il tuo menù pubblico non è visibile ai clienti. Contatta l'assistenza MenuOne per maggiori informazioni.</p>
        </div>
      )}

      {restaurant?.foodtruck?.enabled && (
        <div className="rounded-2xl border border-[#5aa469]/40 bg-[#5aa469]/8 p-5 text-sm" data-testid="foodtruck-summary">
          <span className="font-semibold">FoodTruck attivo:</span>{" "}
          {restaurant.foodtruck.stops?.length > 0
            ? `${restaurant.foodtruck.stops.length} ${restaurant.foodtruck.stops.length === 1 ? "tappa programmata" : "tappe programmate"} · il menù mostra la prossima`
            : <>{restaurant.foodtruck.place}
              {restaurant.foodtruck.date && ` · ${new Date(restaurant.foodtruck.date).toLocaleDateString("it-IT")}`}
              {restaurant.foodtruck.time_from && ` · ${restaurant.foodtruck.time_from}${restaurant.foodtruck.time_to ? `–${restaurant.foodtruck.time_to}` : ""}`}</>}
        </div>
      )}

      {canPay && connect && !connect.charges_enabled && (
        <Link to="/dashboard/settings" className="rounded-2xl border border-amber-300 bg-amber-50 p-5 flex items-center justify-between gap-4 flex-wrap hover:bg-amber-100/70 transition-colors" data-testid="connect-reminder">
          <div className="flex items-center gap-3">
            <ExternalLink className="h-5 w-5 text-amber-600" />
            <div>
              <p className="font-semibold">Collega il tuo conto per incassare i pagamenti al tavolo</p>
              <p className="text-gray-500 text-sm">Finché non colleghi il tuo conto Stripe, i clienti non possono pagare il conto dal menù.</p>
            </div>
          </div>
          <span className="text-amber-700 font-medium text-sm">Vai a Impostazioni →</span>
        </Link>
      )}

      <InstallAppCard />
      {!!user?.subscription?.features?.ordering && <PushNotificationsCard />}

      {!!user?.subscription?.features?.takeaway && restaurant && !restaurant.takeaway?.enabled && (
        <Link to="/dashboard/settings" className="rounded-2xl border border-amber-300 bg-amber-50 p-5 flex items-center justify-between gap-4 flex-wrap hover:bg-amber-100/70 transition-colors" data-testid="takeaway-reminder">
          <div className="flex items-center gap-3">
            <ShoppingBasket className="h-5 w-5 text-amber-600" />
            <div>
              <p className="font-semibold">Attiva le prenotazioni da asporto</p>
              <p className="text-gray-500 text-sm">Il pulsante "Prenota da asporto" appare nel menù solo dopo aver attivato l'asporto e impostato almeno una fascia oraria di ritiro.</p>
            </div>
          </div>
          <span className="text-amber-700 font-medium text-sm">Vai a Impostazioni →</span>
        </Link>
      )}

      {!user?.subscription && (
        <div className="rounded-2xl border border-[#5aa469]/40 bg-[#5aa469]/8 p-6 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <Sparkles className="h-6 w-6 text-[#5aa469]" />
            <div>
              <p className="font-semibold text-lg">Attiva un piano MenuOne</p>
              <p className="text-gray-500 text-sm">Sblocca tutte le funzioni e pubblica il tuo menù senza limiti.</p>
            </div>
          </div>
          <Link to="/dashboard/billing"><Button className="rounded-full bg-[#5aa469] hover:bg-[#4a8a56] text-white font-semibold" data-testid="activate-plan">Scegli un piano</Button></Link>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {[
          { label: "Categorie", value: stats.categories, icon: FolderTree },
          { label: "Piatti", value: stats.dishes, icon: UtensilsCrossed },
          { label: "Piano attivo", value: user?.subscription?.name || ({ free: "Free", basic: "Basic", avanzato: "Avanzato", pro: "Pro", business: "Business", daily: "A Tempo" }[(user?.subscription?.lookup_key || "").split("_")[0]] || "Nessuno"), icon: Sparkles },
        ].map((s) => (
          <div key={s.label} className="rounded-xl mo-card p-6" data-testid={`stat-${s.label.toLowerCase()}`}>
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-500">{s.label}</p>
              <s.icon className="h-5 w-5 text-[#5aa469]" />
            </div>
            <p className="text-3xl font-extrabold mt-2 font-display">{s.value}</p>
          </div>
        ))}
      </div>

      {orderStats && (
        <div className="rounded-2xl mo-card p-6" data-testid="order-stats">
          <div className="flex items-center gap-2 mb-5"><TrendingUp className="h-5 w-5 text-[#5aa469]" /><h3 className="font-display font-semibold text-2xl">Statistiche ordini</h3></div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
            {[
              { label: "Ordini oggi", value: orderStats.orders_today },
              { label: "Incasso oggi", value: fmtMoney(orderStats.revenue_today, orderStats.currency) },
              { label: "Ordini totali", value: orderStats.orders_total },
              { label: "Incasso totale", value: fmtMoney(orderStats.revenue_total, orderStats.currency) },
            ].map((s) => (
              <div key={s.label} className="rounded-xl bg-gray-50 p-4" data-testid={`orderstat-${s.label.toLowerCase().replace(/\s/g, "-")}`}>
                <p className="text-xs text-gray-500">{s.label}</p>
                <p className="text-2xl font-extrabold font-display mt-1">{s.value}</p>
              </div>
            ))}
          </div>
          {orderStats.top_dishes.length > 0 && (
            <div>
              <p className="text-sm text-gray-500 mb-2">Piatti più ordinati</p>
              <div className="space-y-2">
                {orderStats.top_dishes.map((t, i) => (
                  <div key={t.name} className="flex items-center gap-3" data-testid={`top-dish-${i}`}>
                    <span className="text-sm w-5 text-gray-400">{i + 1}.</span>
                    <span className="flex-1 text-sm font-medium text-gray-800">{t.name}</span>
                    <span className="text-sm text-[#4a8a56] font-semibold">{t.count} {t.count === 1 ? "ordine" : "ordini"}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {!!user?.subscription?.features?.export_csv && <ExportCard canPay={canPay} canOrders={!!user?.subscription?.features?.ordering} />}

      {canPay && bills && (
        <div className="rounded-2xl mo-card p-6" data-testid="bills-card">
          <div className="flex items-center gap-2 mb-5"><CreditCard className="h-5 w-5 text-[#5aa469]" /><h3 className="font-display font-semibold text-2xl">Incassi al tavolo</h3></div>
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="rounded-xl bg-gray-50 p-4" data-testid="bills-today"><p className="text-xs text-gray-500">Incasso oggi</p><p className="text-2xl font-extrabold font-display mt-1">{fmtMoney(bills.total_today, restaurant?.currency_code || "eur")}</p></div>
            <div className="rounded-xl bg-gray-50 p-4" data-testid="bills-total"><p className="text-xs text-gray-500">Incasso totale</p><p className="text-2xl font-extrabold font-display mt-1">{fmtMoney(bills.total_all, restaurant?.currency_code || "eur")}</p></div>
          </div>
          {bills.bills.length === 0 ? (
            <p className="text-sm text-gray-400">Nessun pagamento al tavolo ancora ricevuto.</p>
          ) : (
            <div className="space-y-2">
              {bills.bills.slice(0, 8).map((b, i) => (
                <div key={b.id || b.session_id || `${b.date}-${i}`} className="flex items-center justify-between text-sm border-b border-gray-50 pb-2" data-testid={`bill-${i}`}>
                  <span className="text-gray-600">{b.table ? `Tavolo ${b.table}` : "Conto"} · {b.date ? new Date(b.date).toLocaleString("it-IT") : ""}</span>
                  <span className="font-semibold text-[#4a8a56]">{fmtMoney(b.amount, b.currency || restaurant?.currency_code || "eur")}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        <Link to="/dashboard/menu" className="group rounded-2xl mo-card p-6 hover:-translate-y-1 hover:border-[#5aa469]/50 transition-[transform,border-color]">
          <div className="h-11 w-11 rounded-xl bg-[#5aa469]/12 flex items-center justify-center mb-4"><UtensilsCrossed className="h-6 w-6 text-[#5aa469]" /></div>
          <h3 className="font-display font-semibold text-2xl">Modifica il menù</h3>
          <p className="text-gray-500 text-sm mt-1">Aggiungi categorie, piatti, prezzi e foto.</p>
          <span className="inline-flex items-center gap-1 text-[#5aa469] text-sm mt-4 font-medium">Apri <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" /></span>
        </Link>
        <Link to="/dashboard/qr" className="group rounded-2xl mo-card p-6 hover:-translate-y-1 hover:border-[#5aa469]/50 transition-[transform,border-color]">
          <div className="h-11 w-11 rounded-xl bg-[#5aa469]/12 flex items-center justify-center mb-4"><QrCode className="h-6 w-6 text-[#5aa469]" /></div>
          <h3 className="font-display font-semibold text-2xl">Il tuo QR Code</h3>
          <p className="text-gray-500 text-sm mt-1">Scarica e stampa il QR da mettere sui tavoli.</p>
          <span className="inline-flex items-center gap-1 text-[#5aa469] text-sm mt-4 font-medium">Apri <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" /></span>
        </Link>
      </div>

      {restaurant && (
        <div className="rounded-2xl mo-card p-6">
          <p className="text-sm text-gray-500 mb-2">Link pubblico del tuo menù</p>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <a href={menuUrl} target="_blank" rel="noreferrer" className="text-[#5aa469] font-medium break-all hover:underline" data-testid="public-link">{menuUrl}</a>
            <a href={menuUrl} target="_blank" rel="noreferrer"><Button variant="outline" size="sm" className="rounded-full border-gray-300 bg-white hover:bg-gray-50"><ExternalLink className="h-4 w-4 mr-2" />Anteprima</Button></a>
          </div>
        </div>
      )}
    </div>
  );
}
